import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Select, Space, message, Popconfirm } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";

const Departments = () => {
  const { request } = useRequest();
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchDepartments = () => {
    request({ url: apiPath.departments, method: "GET", onSuccess: (res) => setDepartments(res?.data?.data || []) });
  };

  const fetchEmployees = () => {
    request({ url: apiPath.employees, method: "GET", onSuccess: (res) => setEmployees(res?.data?.employees || []) });
  };

  useEffect(() => {
    fetchDepartments();
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({ ...record, headOfDepartment: record.headOfDepartment?._id });
    setModalOpen(true);
  };

  const onSubmit = () => {
    form.validateFields().then((values) => {
      const url = editing ? `${apiPath.departments}/${editing._id}` : apiPath.departments;
      const method = editing ? "PUT" : "POST";
      // Clearing the Select leaves this key undefined, which axios's JSON
      // body would drop entirely — the backend's `$set: req.body` update
      // then simply wouldn't touch the field, so an existing HOD could
      // never be un-assigned. Sending an explicit null fixes that.
      const payload = { ...values, headOfDepartment: values.headOfDepartment || null };
      request({
        url,
        method,
        data: payload,
        onSuccess: () => {
          message.success(editing ? "Department updated" : "Department created");
          setModalOpen(false);
          fetchDepartments();
        },
      });
    });
  };

  const onDelete = (record) => {
    request({
      url: `${apiPath.departments}/${record._id}`,
      method: "DELETE",
      onSuccess: () => {
        message.success("Department deleted");
        fetchDepartments();
      },
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Description", dataIndex: "description" },
    { title: "Head of Department", dataIndex: ["headOfDepartment", "name"] },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title="Delete this department?" onConfirm={() => onDelete(record)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <PageTransition>
      <PageHeader
        title="Departments"
        subtitle="Organize employees into departments"
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Department
          </Button>
        }
      />
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={departments}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: <EmptyState message="No departments yet" actionLabel="Add Department" onAction={openCreate} /> }}
      />

      <Modal title={editing ? "Edit Department" : "Add Department"} open={modalOpen} onCancel={() => setModalOpen(false)} onOk={onSubmit} destroyOnClose>
        <Form layout="vertical" form={form}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="headOfDepartment" label="Head of Department">
            <Select
              allowClear
              showSearch
              placeholder="Select an employee"
              filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
              options={employees.map((e) => ({ value: e._id, label: `${e.name} (${e.employeeId})` }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </PageTransition>
  );
};

export default Departments;
